import { CRITICAL_NETWORK_CALLS } from './resources';
import { getBootStaticResourcePerformanceTiming } from './utils/getBootStaticResourcePerformanceTiming';
import { getBootTimings } from 'owa-performance/lib/utils/trackBootTimings';
import { getLastMarkedMeasure } from 'mini-performance/lib/utils/getLastMarkedMeasure';
import { getOwaWorkload } from 'owa-workloads';
import { getResourcesForWorkload } from './utils/getResourcesForWorkload';
import { getTimeFromFetchStart } from 'mini-performance/lib/utils/getTimeFromFetchStart';
import { getTiming } from 'mini-performance/lib/utils/measureFunctionPerf';
import { getTimingData } from './utils/getTimingData';

const TRACKED_API_RESOURCES = CRITICAL_NETWORK_CALLS.map(resource => resource.name);

/**
 * Get the duration entry for session data loaded in following ways
 * 1. Network call
 * 2. Loaded from cache (Outlook Lite)
 * @param bootTimings
 * @returns
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any --
 * See aka.ms/mini-lint-errors
 *	> Unexpected any. Specify a different type. */
const getSessionDataDuration = (bootTimings: any) => {
    let start: number;
    let end: number;

    // Looks for a perf entry marked from offline session data load for Outlook Lite`
    const measure = getLastMarkedMeasure('offline_sd');
    if (measure) {
        start = 0;
        end = Math.round(measure.duration);
    } else {
        start = getTimeFromFetchStart(bootTimings.sd_s);
        end = getTimeFromFetchStart(bootTimings.sd_e);
    }

    return end ? `${start}_${end}` : null;
};

let firstBootData = null;
const getFirstBootTimingData = () => {
    if (firstBootData) {
        return firstBootData;
    }

    const payload = {};
    const bootTimings = JSON.parse(getBootTimings());
    /* tslint:disable:no-string-literal */
    payload['bt_sd'] = getSessionDataDuration(bootTimings);
    payload['bt_ubs'] =
        getTimeFromFetchStart(bootTimings['ubs_s']) +
        '_' +
        getTimeFromFetchStart(bootTimings['ubs_e']);
    payload['bt_btsp'] =
        getTimeFromFetchStart(bootTimings['btsp_s']) +
        '_' +
        getTimeFromFetchStart(bootTimings['btsp_e']);
    payload['in_e'] = bootTimings['in_e'];
    payload['bt_ren'] =
        getTimeFromFetchStart(bootTimings['ren_s']) +
        '_' +
        getTimeFromFetchStart(bootTimings['ren_e']);

    // Early fetching or runAfterRequests only applies for a full page refresh
    if (getTiming('early_fp')) {
        payload['early_fp'] = Math.floor(getTiming('early_fp'));
    }

    if (getTiming('earlyprocessing')) {
        payload['ep'] =
            getTimeFromFetchStart(Math.floor(getTiming('earlyprocessing_s'))) +
            '_' +
            getTimeFromFetchStart(Math.floor(getTiming('earlyprocessing_e')));
    }
    payload['loc'] = Math.floor(getTiming('localization'));

    firstBootData = payload;
    return firstBootData;
};

const CRITICAL_TIMING_CACHE = new Map();

/**
 * Returns the accumulated data for the tracked resources.
 */
export function getCriticalTimingData() {
    const workload = getOwaWorkload();
    if (CRITICAL_TIMING_CACHE.has(workload)) {
        return CRITICAL_TIMING_CACHE.get(workload);
    }

    const payload = {};

    try {
        // get the performance timing data for critical API resources and boot bundles
        const resourceForWorkload = getResourcesForWorkload(workload);
        [...TRACKED_API_RESOURCES, resourceForWorkload].forEach(resource => {
            const timingData = getTimingData(resource);
            if (timingData) {
                payload[resource] = timingData;
            }
        });

        // First page load timings, like startup-data and shared bootstrap time
        Object.assign(payload, getFirstBootTimingData());

        // Static resource timing, like common vendors and shared bundles
        Object.assign(payload, getBootStaticResourcePerformanceTiming());

        if (getTiming('earlyCalEventsFetch')) {
            payload['early_ce'] =
                getTimeFromFetchStart(Math.floor(getTiming('earlyCalEventsFetch_s'))) +
                '_' +
                getTimeFromFetchStart(Math.floor(getTiming('earlyCalEventsFetch_e')));
        }

        CRITICAL_TIMING_CACHE.set(workload, payload);

        /* tslint:enable:no-string-literal */
    } catch (error) {}

    return payload;
}
